import { useState } from "react";
import Axios from "axios";
import { useNavigate } from 'react-router-dom';
import React from "react";
import CircularProgress from '@mui/material/CircularProgress';

export default function Login() {
  const navigate = useNavigate();
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [noUserFound, setNoUserFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const login = (event) => {
    event.preventDefault();
    setLoading(true);
    setNoUserFound(false);

    Axios({
      method: "POST",
      data: {
        username: loginUsername,
        password: loginPassword,
      },
      withCredentials: true,
      url: `${backEndUrl}/auth`,
    })
    .then((res) => {
      setLoading(false);
      if (res.data !== 'No User Exists') {
        navigate('/upload/' + res.data._id);
      } else {
        setNoUserFound(true);
      }
    })
    .catch((err) => {
      setLoading(false);
      if (err.response && err.response.status === 429) {
        alert(err.response.data.message);
      } else {
        console.error('An error occurred:', err);
        setNoUserFound(true);
      }
    });
  };

  return (
    <div className="flex min-h-screen bg-white">

      {/* Left panel: form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-10 lg:flex-none lg:w-[480px] xl:w-[540px] overflow-hidden">

        {/* Decorative blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -left-32 w-80 h-80 rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #0ea5e9, transparent 70%)' }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #38bdf8, transparent 70%)' }}
        />

        <div className="mx-auto w-full max-w-sm">

          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-sky-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
            </div>
            <span className="text-2xl font-bold text-slate-800 tracking-tight">Client Portal</span>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 leading-tight">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Sign in to access your documents and files.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={login} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-1.5">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={loginUsername}
                onChange={(e) => { setLoginUsername(e.target.value.trim()); setNoUserFound(false); }}
                placeholder="Enter your username"
                className="block w-full rounded-xl border-0 py-2.5 px-4 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-500 sm:text-sm transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={loginPassword}
                onChange={(e) => { setLoginPassword(e.target.value.trim()); setNoUserFound(false); }}
                placeholder="••••••••"
                className="block w-full rounded-xl border-0 py-2.5 px-4 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-500 sm:text-sm transition"
              />
            </div>

            {noUserFound && (
              <div className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 ring-1 ring-red-100">
                <svg className="h-4 w-4 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700 font-medium">Incorrect username or password.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-200 hover:from-sky-400 hover:to-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 active:scale-[0.98] transition-all duration-150 disabled:opacity-60"
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : "Sign in"}
            </button>
          </form>

          <p className="mt-10 text-center text-xs text-slate-400">
            Need help? Contact your consultant.
          </p>
        </div>
      </div>

      {/* Right panel: photo */}
      <div className="relative hidden flex-1 lg:block">
        <img
          alt="Client portal"
          src="https://images.pexels.com/photos/3769021/pexels-photo-3769021.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/60 via-slate-900/40 to-slate-900/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />

      </div>

    </div>
  );
}


